<template>
  <div id="app" class="app">
    <Navbar v-if="userLoggedIn" />
    <router-view />
  </div>
</template>

<script lang="ts">
// @ is an alias to /src
import Navbar from '@/components/navbar.vue';
import router from '@/router';
import store from '@/store';
import axios from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { isAdmin } from '@/api/auth';

@Component({
  name: 'App',
  components: {
    Navbar,
  },
})
class App extends Vue {
  @Getter('userLoggedIn') public userLoggedIn!: boolean;
  public async created() {
    const localStorageLang = process.env.VUE_APP_LOCAL_STORAGE_LANG_PROP;
    const lang = localStorage.getItem(localStorageLang);

    store.commit('setLanguage', lang ? lang : 'en');

    const localStorageAuthProp = process.env.VUE_APP_LOCAL_STORAGE_AUTH_PROP;
    const authString = localStorage.getItem(localStorageAuthProp);

    if (authString) {
      const { userLoggedIn, auth } = JSON.parse(authString);

      if (userLoggedIn && auth?.token) {
        // Temporarily set auth to enable API call
        store.commit('setUserLogin', auth);
        
        // Validate token against API
        try {
          await isAdmin();
          // Token is valid, continue
        } catch {
          // Token is invalid, logout and redirect
          store.commit('setUserLogout');
          localStorage.removeItem(localStorageAuthProp);
          router.push('/login');
        }
      } else {
        router.push('/login');
      }
    } else {
      router.push('/login');
    }
    
    const serverPath = process.env.VUE_APP_SERVER_PATH;
    axios.interceptors.response.use(undefined, (err) => {
      if (err.request.responseURL.includes(serverPath)) {
        if (err.request.status === 401) {
          // if you ever get an unauthorized, logout the user
          store.commit('setUserLogout');
          localStorage.setItem(
            process.env.VUE_APP_LOCAL_STORAGE_REDIRECT_PROP,
            router.currentRoute.fullPath
          );
          router.push('/login');
        }
        if (err.request.status === 403) {
          // User should not see permissions error
          router.push('/login');
        }
      }
      throw err;
    });
  }
}

export default App;
</script>

<style lang="scss">
.app {
  position: relative;
  padding: 0;
  transition: margin 0.3s ease;

  .material-icons {
    font-size: 125%;
  }
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-SemiBold.eot");
  src: url("./assets/fonts/Muli/Muli-SemiBold.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-SemiBold.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-SemiBold.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-SemiBold.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-SemiBold.svg#Muli-SemiBold") format("svg");
  font-weight: 600;
  font-style: normal;
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-Black.eot");
  src: url("./assets/fonts/Muli/Muli-Black.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-Black.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-Black.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-Black.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-Black.svg#Muli-Black") format("svg");
  font-weight: 900;
  font-style: normal;
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-Regular.eot");
  src: url("./assets/fonts/Muli/Muli-Regular.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-Regular.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-Regular.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-Regular.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-Regular.svg#Muli-Regular") format("svg");
  font-weight: 500;
  font-style: normal;
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-ExtraBold.eot");
  src: url("./assets/fonts/Muli/Muli-ExtraBold.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-ExtraBold.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-ExtraBold.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-ExtraBold.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-ExtraBold.svg#Muli-ExtraBold") format("svg");
  font-weight: 800;
  font-style: normal;
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-Medium.eot");
  src: url("./assets/fonts/Muli/Muli-Medium.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-Medium.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-Medium.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-Medium.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-Medium.svg#Muli-Medium") format("svg");
  font-weight: 500;
  font-style: normal;
}

@font-face {
  font-family: "Muli";
  src: url("./assets/fonts/Muli/Muli-Bold.eot");
  src: url("./assets/fonts/Muli/Muli-Bold.eot?#iefix")
      format("embedded-opentype"),
    url("./assets/fonts/Muli/Muli-Bold.woff2") format("woff2"),
    url("./assets/fonts/Muli/Muli-Bold.woff") format("woff"),
    url("./assets/fonts/Muli/Muli-Bold.ttf") format("truetype"),
    url("./assets/fonts/Muli/Muli-Bold.svg#Muli-Bold") format("svg");
  font-weight: 900;
  font-style: normal;
}
</style>